import React from 'react';

import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn, FieldValues, FieldPath } from 'react-hook-form';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';

import {
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';

type SelectOption = {
    value: string;
    label: string;
};

type SelectFormFieldProps<TData extends FieldValues> = {
    form: UseFormReturn<TData>;
    name: FieldPath<TData>;
    label: string;
    options?: string[] | SelectOption[];
    optionTranslationMap?: {
        [key: string]: MessageDescriptor;
    };
    isBoolean?: boolean;
    required?: boolean;
    disabled?: boolean;
    onChange?: (v: string) => void;
    variant?: 'dialog' | 'default';
    tooltip?: string;
    description?: string;
};

const messages = defineMessages({
    clearSelection: {
        id: 'clear-selection',
        defaultMessage: 'Clear selection',
    },
});

const CLEAR_SELECT = 'clear_select';

export default function SelectFormField<TData extends FieldValues>({
    variant = 'dialog',
    required = true,
    options = [],
    name,
    label,
    form,
    isBoolean,
    optionTranslationMap,
    onChange,
    disabled,
    tooltip,
    description,
}: SelectFormFieldProps<TData>) {
    const { formatMessage } = useIntl();
    const [clearKey, setClearKey] = React.useState(0);

    const normalizedOptions: SelectOption[] = React.useMemo(
        () =>
            options?.map((option) =>
                typeof option === 'string'
                    ? {
                          value: option,
                          label: option,
                      }
                    : option,
            ),
        [options],
    );

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => {
                return (
                    <FormItem variant={variant} key={clearKey}>
                        <FormLabel variant={variant} required={required}>
                            {label}
                        </FormLabel>
                        {tooltip && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger type="button" className="mb-0 ml-1 text-gray-400">
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-md">{tooltip}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        <FormControlStack>
                            <Select
                                key={clearKey}
                                onValueChange={(value) => {
                                    if (value === CLEAR_SELECT) {
                                        field.onChange(undefined);
                                        setClearKey(() => clearKey + 1);
                                        return;
                                    }
                                    const newValue = isBoolean ? JSON.parse(value) : value;
                                    if (onChange) onChange(newValue);
                                    field.onChange(newValue);
                                }}
                                value={field.value?.toString()}
                            >
                                <FormControl>
                                    <SelectTrigger className="w-full" disabled={disabled}>
                                        <SelectValue placeholder={`${label}...`} />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value={CLEAR_SELECT}>
                                        {formatMessage(messages.clearSelection)}
                                    </SelectItem>
                                    <Separator />
                                    {normalizedOptions.map(({ value, label }) => (
                                        <SelectItem
                                            key={value}
                                            value={value}
                                            onPointerDown={() => {
                                                const newValue = isBoolean ? JSON.parse(value) : value;

                                                if (newValue === field.value?.toString()) {
                                                    field.onChange(undefined);
                                                    setClearKey(() => clearKey + 1);
                                                    return;
                                                }
                                            }}
                                        >
                                            {optionTranslationMap ? formatMessage(optionTranslationMap[label]) : label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {description && <FormDescription>{description}</FormDescription>}
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                );
            }}
        />
    );
}
